import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import { getSystem } from '../../services/systemService';
import { getRelativeFileUrl } from '../../services/relativeFilesService';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import logoPlaceholder from '../../assets/placeholder.jpg';

interface SystemDetail {
  id: string;
  name: string;
  description?: string;
  base_url: string;
  logo_url?: string; // Campo opcional para a URL da logo
}

const SystemDetailView: React.FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const [system, setSystem] = useState<SystemDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchSystem = async () => {
    setLoading(true);
    try {
      if (systemId) {
        const data = await getSystem(systemId);
        setSystem(data);
      }
    } catch (error) {
      console.error('Erro ao buscar sistema:', error);
      swal('Erro', 'Não foi possível buscar os detalhes do sistema.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystem();
  }, [systemId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!system) {
    return (
      <Typography variant="h6" color="error">
        Sistema não encontrado.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}
      >
        <Typography variant="h4">Detalhes do Sistema</Typography>
        {user?.is_system_admin && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/sistemas/${system.id}/editar`)}
          >
            Editar Sistema
          </Button>
        )}
      </Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {system.logo_url ? (
            <Box
              component="img"
              src={getRelativeFileUrl(system.logo_url)}
              alt={`${system.name} Logo`}
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                objectFit: 'contain',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
          ) : (
            <Box
              component="img"
              src={logoPlaceholder}
              alt="Logo Padrão"
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                objectFit: 'contain',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
          )}
          <Typography variant="h5">{system.name}</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="h6">Descrição:</Typography>
        <Typography variant="body1" gutterBottom>
          {system.description || '—'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6">URL Base:</Typography>
        <Typography variant="body1" gutterBottom>
          <a href={system.base_url} target="_blank" rel="noopener noreferrer">
            {system.base_url}
          </a>
        </Typography>
      </Paper>
    </Box>
  );
};

export default SystemDetailView;